/**
 * Helpers for reasoning about the lifetime of the current access token.
 *
 * The auth API returns the token's expiry in the `expires` field, which we
 * persist (Redux + localStorage) alongside the token. The backend sends it as an
 * absolute ISO 8601 UTC timestamp (millisecond precision) — NOT a relative TTL.
 * This matches the confirmed shape of the sibling sessions API, which returns
 * e.g. `"created_at":"2026-06-16T13:38:05.299Z"` /
 * `"expires_at":"2026-06-23T13:38:05.298Z"` (see `UserSession` in
 * `@/store/slices/userSessions`). We additionally accept a numeric epoch (seconds
 * or ms) defensively, so a format change on the backend degrades gracefully to
 * "always refresh" instead of silently breaking the gate that consumes this.
 */

/**
 * Refresh the access token only once it has less than this much lifetime left.
 * "Less than a minute remaining" per product requirement.
 */
export const REFRESH_THRESHOLD_MS = 60_000;

/**
 * Remaining lifetime of the access token in milliseconds, derived from the
 * server-provided `expires` value. Negative when the token has already expired.
 *
 * Returns `null` when `expires` is absent or cannot be parsed — callers should
 * treat an unknown lifetime as "needs refresh" so we never get stuck holding a
 * token we can't reason about.
 */
export const getAccessTokenRemainingMs = (expires: string | null | undefined): number | null => {
  if (expires == null || expires === '') return null;

  // Primary: an absolute timestamp string (ISO 8601 etc.).
  let expiryMs = Date.parse(expires);

  // Fallback: a bare numeric epoch. Disambiguate seconds vs milliseconds by
  // magnitude — anything below ~year 2001 expressed in ms must really be seconds.
  if (Number.isNaN(expiryMs)) {
    const epoch = Number(expires);
    if (!Number.isFinite(epoch) || epoch <= 0) return null;
    expiryMs = epoch < 1e12 ? epoch * 1000 : epoch;
  }

  return expiryMs - Date.now();
};
